import { requestJson, withQuery } from "../http/httpClient";
import { isJsonObject, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { ITEM_KINDS, type ItemKind } from "./reviewDecoders";
import { readableJsonText } from "../../shared/formatting/readableJson";

/**
 * One project item at one exact version (`GET /content/items/{version ref}`): an ontology
 * module, shapes, a competency question, a few-shot example or a definition. The web app
 * shows it as its stored text: canonical Turtle, or the content as formatted JSON.
 */
export interface ItemVersion {
  readonly reference: string;
  readonly itemId: string;
  readonly kind: ItemKind;
  readonly name: string;
  readonly description?: string | null;
  readonly text: string;
  readonly textIsTurtle: boolean;
  /** The content of a process definition (prompt, skill, artifact, …), as stored. */
  readonly definition: JsonObject | null;
  /** A prompt's text or a skill's instructions: the Markdown the item is written in, read as text; `null` for other items. */
  readonly markdown: string | null;
}

/** Where a definition keeps its Markdown body: a prompt's `text`, a skill's `body` (prompts.py, skills.py). */
const MARKDOWN_BODIES: Partial<Record<ItemKind, string>> = { prompt: "text", skill: "body" };

export function getItemVersion(reference: string, signal?: AbortSignal): Promise<ItemVersion> {
  return requestJson({
    method: "GET",
    path: `/content/items/${encodeURIComponent(reference).replaceAll("%40", "@")}`,
    signal,
    decodeResponse: readItemVersion,
  });
}

/**
 * The item with this ID at one commit of the project content, such as a change set's head
 * (`GET /projects/{project}/content/items/by-id/{item}`): one read, where listing every item
 * would page through all of them. `resource_not_found` when the commit holds no such item.
 */
export function getItemVersionById(projectId: string, itemId: string, commit: string, signal?: AbortSignal): Promise<ItemVersion> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/items/by-id/${encodeURIComponent(itemId)}`, { version: commit }),
    signal,
    decodeResponse: readItemVersion,
  });
}

function readItemVersion(body: unknown): ItemVersion {
  const item = readObject(body, "item version");
  const rdf = item["rdf"] === null || item["rdf"] === undefined ? null : readObject(item["rdf"], "RDF item");
  const content = item["competency_question"] ?? item["few_shot_example"] ?? item["definition"] ?? null;
  const itemReference = readString(item, "reference");
  const kind = readOneOf(item, "kind", ITEM_KINDS);
  const definition = isJsonObject(item["definition"]) ? item["definition"] : null;
  const markdownField = MARKDOWN_BODIES[kind];
  const markdown = markdownField === undefined || definition === null ? undefined : definition[markdownField];
  return {
    reference: itemReference,
    itemId: itemReference.split("@")[0] ?? itemReference,
    kind,
    name: readString(item, "name"),
    description: typeof item["description"] === "string" ? item["description"] : null,
    text: rdf !== null ? readString(rdf, "turtle") : readableJsonText(content),
    textIsTurtle: rdf !== null,
    definition,
    markdown: typeof markdown === "string" ? markdown : null,
  };
}

/** The two sides of a changed item. An added item has no `before`, a removed one no `after`. */
export interface ItemVersionPair {
  readonly before: ItemVersion | null;
  readonly after: ItemVersion | null;
}

/** Both versions of a change, read side by side; a missing reference reads as `null`. */
export async function getItemVersionPair(before: string | null, after: string | null, signal?: AbortSignal): Promise<ItemVersionPair> {
  const read = (reference: string | null) => (reference === null ? Promise.resolve(null) : getItemVersion(reference, signal));
  const [beforeVersion, afterVersion] = await Promise.all([read(before), read(after)]);
  return { before: beforeVersion, after: afterVersion };
}
